import type { RadiologyFormState } from '../radiologyFormData'
import type { RadiologyFacility } from '../radiologyFacilities'
import type { OrderPdfData, OrderPdfSection, AutoPopulateData } from './pdfTypes'
import { buildPatientSelectedLocationPdfFields } from './patientSelectedLocation'

export function mapRadiologyToPdf(
  form: RadiologyFormState,
  auto: AutoPopulateData,
  facility: RadiologyFacility | undefined,
  logoDataUrl?: string,
  /** PD-1642 — patient will choose any imaging location; nothing was faxed. */
  patientSelectedLocation?: boolean,
): OrderPdfData {
  const icd10Codes = form.diagnosisText
    ? form.diagnosisText
        .split(/[;\n]/)
        .map((s) => s.trim())
        .filter(Boolean)
    : []

  const vendorSections: OrderPdfSection[] = []

  // Facility header (rendered in the PDF header area, not as a body section)
  const facilityHeader = facility
    ? {
        name: facility.name,
        address: `${facility.address}, ${facility.city}, ${facility.state} ${facility.zip}`,
        fax: facility.fax || 'N/A',
        logoDataUrl,
      }
    : undefined

  // Clinical information
  const clinicalData: Record<string, string> = {}
  if (form.clinicalNotes) clinicalData['Clinical Indication'] = form.clinicalNotes
  if (form.relevantHistory) clinicalData['Relevant History'] = form.relevantHistory
  if (form.isPregnant)
    clinicalData['Pregnant'] =
      form.isPregnant === 'yes' ? 'Yes' : form.isPregnant === 'no' ? 'No' : 'N/A'
  if (form.contrastAllergy) clinicalData['Contrast Allergy'] = 'Yes'
  if (form.priorAuthCode) clinicalData['Prior Auth Code'] = form.priorAuthCode
  if (Object.keys(clinicalData).length > 0) {
    vendorSections.push({ title: 'Clinical Information', type: 'keyValue', data: clinicalData })
  }

  return {
    vendorName: 'Radiology',
    orderTitle: 'Radiology Order Requisition',
    facilityHeader,
    patient: {
      firstName: auto.patient.firstName,
      middleName: auto.patient.middleName,
      lastName: auto.patient.lastName,
      dateOfBirth: auto.patient.dateOfBirth,
      sex: auto.patient.sexCode,
      email: auto.patient.email,
      phone: auto.patient.phone,
      address: auto.patient.address,
      city: auto.patient.city,
      state: auto.patient.state,
      zip: auto.patient.zip,
      country: auto.patient.country,
      patientId: auto.patient.patientId,
    },
    provider: {
      organizationName: auto.provider.organizationName,
      providerName: auto.provider.providerName,
      npi: auto.provider.npi,
      phone: auto.provider.orgPhone,
      fax: auto.provider.orgFax,
      address: auto.provider.orgAddress,
      city: auto.provider.orgCity,
      state: auto.provider.orgState,
      zip: auto.provider.orgZip,
      email: auto.provider.providerEmail,
    },
    insurance: {
      primaryCarrier: auto.insurance.primaryCarrier,
      primaryMemberId: auto.insurance.primaryMemberId,
      primaryGroup: auto.insurance.primaryGroup,
      policyholderName: auto.insurance.policyholderName,
      relationship: auto.insurance.relationship,
    },
    icd10Codes,
    selectedTests: form.selectedProcedures.map((proc) => ({
      code: proc.code,
      name: proc.name,
      category: proc.modality,
    })),
    vendorSections,
    signatureDataUrl: form.providerSignatureDataUrl,
    signatureDate: form.providerSignatureDate,
    ...(patientSelectedLocation
      ? buildPatientSelectedLocationPdfFields('Radiology', auto)
      : undefined),
  }
}
